import React, { useState } from "react";
import Message from "@/model/interface/Message";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import StatusIcon from "./StatusIcon";
import MediaAttachment from "./MediaAttachment";
import { devicePlatformLabel } from "@/util/device";
import { memberName } from "@/util/member";
import { StickyNote, Plus, Pencil, Trash2, X, Check, ShieldAlert, MoreVertical } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  useCreateMessageNoteMutation,
  useUpdateMessageNoteMutation,
  useDeleteMessageNoteMutation,
  useConfirmPossibleOptOutMutation,
  useDismissPossibleOptOutMutation,
} from "@/store/rest_api";

interface MessageBubbleProps {
  message: Message;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message }) => {
  const isOutbound = message.direction === "OUTBOUND";
  const [showNotes, setShowNotes] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [newNoteBody, setNewNoteBody] = useState("");
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState("");

  const [createNote] = useCreateMessageNoteMutation();
  const [updateNote] = useUpdateMessageNoteMutation();
  const [deleteNote] = useDeleteMessageNoteMutation();
  const [confirmOptOut, { isLoading: isConfirming }] = useConfirmPossibleOptOutMutation();
  const [dismissOptOut, { isLoading: isDismissing }] = useDismissPossibleOptOutMutation();

  const hasNotes = message.notes?.length > 0;
  const awaitingOptOutReview = !isOutbound && message.possibleOptOut === true;

  const handleConfirmOptOut = async () => {
    try {
      await confirmOptOut(message.id).unwrap();
      toast.success("Opted out. This number won't receive texts or automated calls.");
    } catch (error: any) {
      toast.error(error?.detail || "Couldn't record the opt-out");
    }
  };

  const handleDismissOptOut = async () => {
    try {
      await dismissOptOut(message.id).unwrap();
      toast.success("Marked as not an opt-out request");
    } catch (error: any) {
      toast.error(error?.detail || "Couldn't dismiss the review");
    }
  };

  const handleAddNote = async () => {
    if (!newNoteBody.trim()) return;
    await createNote({ messageId: message.id, body: newNoteBody.trim() });
    setNewNoteBody("");
    setAddingNote(false);
  };

  const handleUpdateNote = async (noteId: string) => {
    if (!editBody.trim()) return;
    await updateNote({ noteId, body: editBody.trim() });
    setEditingNoteId(null);
    setEditBody("");
  };

  const handleDeleteNote = async (noteId: string) => {
    await deleteNote(noteId);
  };

  return (
    <div className={cn("flex flex-col gap-1", isOutbound ? "items-end" : "items-start")}>
      <div className="max-w-[80%]">
        <div
          className={cn(
            "rounded-xl px-3 py-2 text-sm leading-relaxed",
            isOutbound
              ? "rounded-br-sm bg-primary text-primary-foreground"
              : "rounded-bl-sm bg-muted text-foreground",
          )}
        >
          {message.media?.map((media) => (
            <div key={media.id} className="mb-1">
              <MediaAttachment media={media} />
            </div>
          ))}
          {message.body && (
            <p className="whitespace-pre-wrap break-words">{message.body}</p>
          )}
        </div>

        <div
          className={cn(
            "mt-0.5 flex items-center gap-1 text-[10px] text-muted-foreground",
            isOutbound ? "justify-end" : "justify-start",
          )}
        >
          {isOutbound && <StatusIcon status={message.status} errorCode={message.errorCode} />}
          <span>{format(new Date(message.createdAt), "h:mm a")}</span>
          {message.platform && <span>· {devicePlatformLabel(message.platform)}</span>}
        </div>

        {/* Possible opt-out: the words suggested "stop" but not clearly enough to act on alone.
            Revocation law puts the burden on us, so it is surfaced for a person rather than ignored. */}
        {awaitingOptOutReview && (
          <div className="mt-1.5 flex flex-col gap-1.5 rounded-md border border-border bg-background px-2.5 py-2">
            <span className="status-pill-warning self-start">
              <ShieldAlert className="h-3 w-3" />
              Possible opt-out
            </span>
            <p className="text-[11px] leading-snug text-muted-foreground">
              This might be a request to stop texting. Review it before contacting them again.
            </p>
            <div className="flex flex-wrap items-center gap-1.5">
              <Button
                size="sm"
                variant="destructive"
                className="h-6 text-xs"
                onClick={handleConfirmOptOut}
                disabled={isConfirming || isDismissing}
              >
                Opt them out
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 text-xs"
                onClick={handleDismissOptOut}
                disabled={isConfirming || isDismissing}
              >
                Not a request
              </Button>
            </div>
          </div>
        )}

        {/* Note indicator / toggle */}
        <div className={cn("flex items-center gap-1 mt-0.5", isOutbound ? "justify-end" : "justify-start")}>
          {hasNotes && (
            <button
              onClick={() => setShowNotes(!showNotes)}
              className="flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400 hover:underline"
            >
              <StickyNote className="h-3 w-3" />
              {message.notes.length} {message.notes.length === 1 ? "note" : "notes"}
            </button>
          )}
          <button
            onClick={() => {
              if (!hasNotes) setShowNotes(true);
              setAddingNote(true);
            }}
            className="text-muted-foreground hover:text-foreground transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
            title="Add note"
          >
            <Plus className="h-3 w-3" />
          </button>
        </div>

        {/* Notes panel */}
        {showNotes && (
          <div className="mt-1 space-y-1">
            {message.notes?.map((note) => (
              <div
                key={note.id}
                className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 px-2.5 py-1.5"
              >
                {editingNoteId === note.id ? (
                  <div className="flex-1 space-y-1">
                    <Textarea
                      value={editBody}
                      onChange={(e) => setEditBody(e.target.value)}
                      className="text-xs min-h-[40px] resize-none bg-white dark:bg-background"
                      autoFocus
                    />
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="ghost" className="h-5 w-5" onClick={() => handleUpdateNote(note.id)}>
                        <Check className="h-3 w-3" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-5 w-5" onClick={() => setEditingNoteId(null)}>
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] text-amber-800 dark:text-amber-300 whitespace-pre-wrap break-words">
                        {note.body}
                      </p>
                      <p className="text-[10px] text-amber-600/70 dark:text-amber-400/70 mt-0.5">
                        {memberName(note.membership)}
                        {" · "}
                        {format(new Date(note.createdAt), "MMM d, HH:mm")}
                      </p>
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            title="Options"
                            className="text-amber-600/70 hover:text-amber-800 dark:text-amber-400/70 dark:hover:text-amber-300"
                          >
                            <MoreVertical className="h-3 w-3" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              setEditingNoteId(note.id);
                              setEditBody(note.body);
                            }}
                          >
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit note
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-destructive" onClick={() => handleDeleteNote(note.id)}>
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete note
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </>
                )}
              </div>
            ))}

            {/* Add note form */}
            {addingNote && (
              <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 px-2.5 py-1.5 space-y-1">
                <Textarea
                  value={newNoteBody}
                  onChange={(e) => setNewNoteBody(e.target.value)}
                  placeholder="Add an internal note..."
                  className="text-xs min-h-[40px] resize-none bg-white dark:bg-background"
                  autoFocus
                />
                <div className="flex items-center gap-1">
                  <Button size="sm" className="h-6 text-xs" onClick={handleAddNote} disabled={!newNoteBody.trim()}>
                    Save
                  </Button>
                  <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => { setAddingNote(false); setNewNoteBody(""); }}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {!addingNote && (
              <button
                onClick={() => setAddingNote(true)}
                className="flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400 hover:underline px-1"
              >
                <Plus className="h-3 w-3" />
                Add note
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;
